import React, { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSession } from "../context/SessionContext";
import { useEmailConnectionRedirect } from "../hooks/useEmailConnectionRedirect";
import { useAdminStatus } from "../hooks/useAdminStatus";
import { useSessionBootstrap } from "../hooks/useSessionBootstrap";
import { BillingNotice } from "./BillingNotice";
import { billingEstaDesatualizado } from "../lib/checkoutIntent";
import { LoadingScreen } from "./LoadingScreen";
import { PATHS } from "./paths";

// Routes where the metadata completeness check should not apply
const METADATA_EXEMPT_PATHS: string[] = [PATHS.onboarding, PATHS.checkoutConfirming];

/** Rota de layout: protege todas as rotas filhas (renderizadas no `<Outlet />`). */
export const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const { metadata, billing } = useSession();
  const location = useLocation();

  // Captura o retorno do fluxo de conexão de email (Google OAuth), que pode
  // aterrissar em qualquer rota autenticada dependendo do APP_URL configurado
  // no backend.
  useEmailConnectionRedirect();

  // Verifica (uma vez) se o usuário logado é admin e guarda em `adminStatus`
  // no contexto — consumido pelo item de menu e pelo guard da rota /admin.
  useAdminStatus();

  const isExemptPath = METADATA_EXEMPT_PATHS.includes(location.pathname);
  // Contexto já hidratado (por um bootstrap anterior, ou pelo onboarding que
  // acabou de rodar) — pula a chamada inteira nesta montagem.
  // Depois do Stripe o contexto pode ter billing "free"/plano null hidratado
  // cedo demais — `billingPrecisaAtualizar` força um GET /billing/assinatura novo.
  const alreadyBootstrapped =
    Boolean(metadata.crm) && billing !== null && !billingEstaDesatualizado();
  const { status, doctor } = useSessionBootstrap(isExemptPath || alreadyBootstrapped);

  // Save the intended destination so AuthCallbackPage can restore it after login
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      sessionStorage.setItem("returnTo", location.pathname + location.search);
    }
  }, [isLoading, isAuthenticated, location.pathname, location.search]);

  // ── Render logic ─────────────────────────────────────────────────────────

  // Auth0 is still initialising
  if (isLoading) return <LoadingScreen />;

  // Not authenticated — save destination and redirect to login flow
  if (!isAuthenticated) return <Navigate to={PATHS.root} replace />;

  // The onboarding page renders unconditionally once authenticated — it doesn't
  // need a complete profile/billing to be reached (it's the page that fixes that).
  if (isExemptPath) return <Outlet />;

  // Context already hydrated from a previous bootstrap — render immediately.
  if (alreadyBootstrapped) return (
    <>
      <BillingNotice />
      <Outlet />
    </>
  );

  // Waiting on GET /doctors/{email} and/or GET /billing/assinatura.
  if (status === "loading") return <LoadingScreen />;

  // Doctor not found or API error — let auth-callback re-evaluate the session
  if (status === "error") return <Navigate to={PATHS.authCallback} replace />;

  // Profile incomplete or organização pendente — send to onboarding with doctor data
  if (status === "needs-onboarding") {
    return <Navigate to={PATHS.onboarding} state={{ doctor }} replace />;
  }

  return (
    <>
      <BillingNotice />
      <Outlet />
    </>
  );
};
